import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { IOffer } from '@rbi-ctg/menu';
import { useGetOfferByIdQuery } from 'generated/sanity-graphql';
import useRoutes from 'hooks/use-routes';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLogger } from 'state/logger';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { isLoyaltyOffer } from 'state/loyalty/types';
import { RedemptionMethod, useOffersContext } from 'state/offers';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { isMobile } from 'utils/environment';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import {
  checkOfferIsAvailableForServiceModeCategory,
  getUniqIdForOffer,
  isCurrentlySelectedOffer,
  makeUrlForOffer,
  offerIsDeliveryOnly,
  offerIsPickupOnly,
} from 'utils/offers';
import { routes } from 'utils/routing';
import { determineServiceModeCategory } from 'utils/service-mode';

import { useOffersUIContext } from './offers-ui-context';
import { IAttemptOfferRedemptionArgs } from './types';

type IsDialogOpen = boolean;
interface IUseOfferRedemptionFlowArgs {
  offer: IOffer;
  authenticated: boolean;
  offerAvailableRightNow: boolean;
  mobileRedemptionAvailable: boolean;
  restaurantRedemptionAvailable: boolean;
  loginRequiredForRedemption: boolean;
}
export const useOfferRedemptionFlow = ({
  offer,
  authenticated,
  offerAvailableRightNow,
  mobileRedemptionAvailable,
  restaurantRedemptionAvailable,
  loginRequiredForRedemption,
}: IUseOfferRedemptionFlowArgs) => {
  const logger = useLogger();
  const {
    inRestaurantLoyaltyEnabledAtRestaurant,
    inRestaurantRedemptionEnabled,
    addInRestaurantRedemptionEntry,
    enableOfferRedemption,
    enableLoyaltyRedeemAllStores,
    hasLegacyOffer,
  } = useInRestaurantRedemptionContext();
  const {
    selectedOffer,
    selectOfferById,
    clearSelectedOffer,
    onSelectMobileRedemption,
    onSelectRestaurantRedemption,
    onCancelRedemption,
    onConfirmRedemption,
    setRedeemedOffer,
    redemptionInProgress,
    setRedemptionInProgress,
  } = useOffersContext();

  const { offersUrl } = useOffersUIContext();
  const { serviceMode, setServiceMode } = useServiceModeContext();
  const { noStoreSelected } = useStoreContext();
  const { setStoreLocatorCallbackUrl } = useLocationContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const { setOriginLocation } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [showConfirmSwitchOffersDialog, setShowConfirmSwitchOffersDialog] = useState(false);
  const [showSwitchServiceModeDialog, setShowSwitchServiceModeDialog] = useState(false);
  const [showReadyToRedeemDialog, setShowReadyToRedeemDialog] = useState(false);
  const [showRestaurantRedemptionModal, setShowRestaurantRedemptionModal] = useState(false);
  const [tentativeRedemptionMethod, setTentativeRedemptionMethod] =
    useState<RedemptionMethod | null>(null);
  const [restaurantRedemptionStartTime, setRestaurantRedemptionStartTime] = useState(0);
  const uniqOfferId = useMemo(
    () => getUniqIdForOffer({ id: offer._id, token: offer.tokenId }),
    [offer]
  );
  const { data: offerData, loading: loadingOfferConfigs } = useGetOfferByIdQuery({
    variables: { id: offer._id },
  });

  const redemptionMethod = offerData?.Offer?.redemptionMethod;
  const shouldForceRestaurantSelection = useFlag(
    LaunchDarklyFlag.FORCE_RESTAURANT_SELECTION_FOR_REWARDS
  );
  const checkForAnotherSelectedOffer = useCallback((): IsDialogOpen => {
    // If an existing offer has already been selected, warn user
    if (selectedOffer && !isCurrentlySelectedOffer(uniqOfferId, selectedOffer)) {
      setShowConfirmSwitchOffersDialog(true);
      return true;
    }

    selectOfferById(uniqOfferId);
    return false;
  }, [selectOfferById, selectedOffer, uniqOfferId]);

  const redeemMobileOffer = useCallback(
    (bypassCheckForAnotherOffer: boolean) => {
      if (!authenticated) {
        setOriginLocation(location.pathname);
        navigate(getLocalizedRouteForPath(routes.signUp));

        return;
      }

      if (!mobileRedemptionAvailable) {
        logger.warn(`Attempting to redeem mobile offer that is not available: offer ${offer._id}`);
        return;
      }

      setTentativeRedemptionMethod(RedemptionMethod.mobile);

      if (!bypassCheckForAnotherOffer) {
        const isDialogOpen = checkForAnotherSelectedOffer();
        if (isDialogOpen) {
          return;
        }
      }

      const nextUrl = makeUrlForOffer(offer) || routes.menu;
      if (!nextUrl) {
        logger.warn(
          `Attempting to make a url for mobile offer that is missing critical data: offer ${offer._id}`
        );
        return;
      }
      onSelectMobileRedemption();
      setTentativeRedemptionMethod(null);

      // If there is no store selected, send user to store selection
      if (noStoreSelected) {
        setStoreLocatorCallbackUrl(nextUrl);
        let path = 'store-locator';
        const deliveryOnly = offerIsDeliveryOnly(offer);
        const pickupOnly = offerIsPickupOnly(offer);
        // If the offer is for only pickup or only delivery
        // skip service mode selection
        if (deliveryOnly || pickupOnly) {
          path = deliveryOnly ? routes.address : routes.locationService;
        }
        navigate(getLocalizedRouteForPath(path));
        return;
      }
      navigate(nextUrl);
    },
    [
      authenticated,
      checkForAnotherSelectedOffer,
      getLocalizedRouteForPath,
      location.pathname,
      logger,
      mobileRedemptionAvailable,
      navigate,
      noStoreSelected,
      offer,
      onSelectMobileRedemption,
      setOriginLocation,
      setStoreLocatorCallbackUrl,
    ]
  );

  const checkIsCBAOffer = useCallback((offer: IOffer) => {
    return !isLoyaltyOffer(offer);
  }, []);

  const redeemRestaurantOffer = useCallback(
    (bypassCheckForAnotherOffer: boolean) => {
      if (!restaurantRedemptionAvailable) {
        logger.warn(
          `Attempting to redeem restaurant offer that is not available: offer ${offer._id}`
        );
        return;
      }

      if (loginRequiredForRedemption && !authenticated) {
        setOriginLocation(location.pathname);
        navigate(getLocalizedRouteForPath(routes.signIn));

        return;
      }

      if (shouldForceRestaurantSelection && noStoreSelected) {
        setStoreLocatorCallbackUrl(location.pathname);
        let path = 'store-locator';
        const deliveryOnly = offerIsDeliveryOnly(offer);
        const pickupOnly = offerIsPickupOnly(offer);
        // Skip service mode selection if offer is for pickup or delivery only
        if (deliveryOnly || pickupOnly) {
          path = deliveryOnly ? routes.address : routes.locationService;
        }

        setRedemptionInProgress(true);
        navigate(getLocalizedRouteForPath(path));
        return;
      }

      setTentativeRedemptionMethod(RedemptionMethod.restaurant);

      if (!bypassCheckForAnotherOffer) {
        const inResturantLoyaltyEnabled =
          inRestaurantLoyaltyEnabledAtRestaurant || enableLoyaltyRedeemAllStores;

        if (
          inResturantLoyaltyEnabled &&
          inRestaurantRedemptionEnabled &&
          enableOfferRedemption &&
          !checkIsCBAOffer(offer)
        ) {
          const offerToAdd = (offerData?.Offer as IOffer) || offer;
          addInRestaurantRedemptionEntry({ offer: offerToAdd });
          return;
        } else {
          const isDialogOpen = checkForAnotherSelectedOffer();
          if (isDialogOpen) {
            return;
          }
        }
      }

      setTentativeRedemptionMethod(null);

      onSelectRestaurantRedemption();
      setShowReadyToRedeemDialog(true);
    },
    [
      restaurantRedemptionAvailable,
      loginRequiredForRedemption,
      authenticated,
      shouldForceRestaurantSelection,
      noStoreSelected,
      onSelectRestaurantRedemption,
      logger,
      offer,
      setOriginLocation,
      location.pathname,
      navigate,
      getLocalizedRouteForPath,
      setStoreLocatorCallbackUrl,
      setRedemptionInProgress,
      inRestaurantLoyaltyEnabledAtRestaurant,
      enableLoyaltyRedeemAllStores,
      inRestaurantRedemptionEnabled,
      enableOfferRedemption,
      offerData,
      addInRestaurantRedemptionEntry,
      checkForAnotherSelectedOffer,
    ]
  );

  const attemptOfferRedemption = useCallback(
    ({ redemptionMethod, bypassCheckForAnotherOffer = false }: IAttemptOfferRedemptionArgs) => {
      if (!offerAvailableRightNow) {
        logger.warn(`Attempting to redeem offer that is not available: offer ${offer._id}`);
      }

      // verify offer is available for service mode, if selected
      if (serviceMode) {
        const serviceModeCategory = determineServiceModeCategory(serviceMode);

        const isAvailableForServiceMode = checkOfferIsAvailableForServiceModeCategory(
          offer,
          serviceModeCategory
        );
        if (!isAvailableForServiceMode) {
          setShowSwitchServiceModeDialog(true);
          return;
        }
      }
      if (redemptionMethod === RedemptionMethod.mobile) {
        redeemMobileOffer(bypassCheckForAnotherOffer);
      } else {
        redeemRestaurantOffer(bypassCheckForAnotherOffer);
      }
    },
    [logger, offer, offerAvailableRightNow, redeemMobileOffer, redeemRestaurantOffer, serviceMode]
  );

  const onConfirmSwitchOffers = useCallback(() => {
    setShowConfirmSwitchOffersDialog(false);
    if (tentativeRedemptionMethod) {
      clearSelectedOffer();
      selectOfferById(uniqOfferId);
      attemptOfferRedemption({
        redemptionMethod: tentativeRedemptionMethod,
        bypassCheckForAnotherOffer: true,
      });
    }
  }, [
    attemptOfferRedemption,
    clearSelectedOffer,
    selectOfferById,
    tentativeRedemptionMethod,
    uniqOfferId,
  ]);

  const onCancelSwitchOffers = useCallback(() => {
    setShowConfirmSwitchOffersDialog(false);
  }, []);

  const onConfirmSwitchServiceMode = useCallback(() => {
    setShowSwitchServiceModeDialog(false);
    const nextUrl = makeUrlForOffer(offer) || routes.menu;
    if (!nextUrl) {
      logger.warn(
        `Attempting to make a url for mobile offer that is missing critical data: offer ${offer._id}`
      );
      return;
    }
    const deliveryOnly = offerIsDeliveryOnly(offer);
    if (deliveryOnly) {
      setStoreLocatorCallbackUrl(nextUrl);
      navigate(getLocalizedRouteForPath(routes.address));
      clearSelectedOffer();
      selectOfferById(uniqOfferId);
      return;
    }
    setStoreLocatorCallbackUrl(nextUrl);
    setServiceMode(ServiceMode.TAKEOUT);
    clearSelectedOffer();
    selectOfferById(uniqOfferId);
    navigate(routes.locationService);
  }, [
    clearSelectedOffer,
    getLocalizedRouteForPath,
    logger,
    navigate,
    offer,
    selectOfferById,
    setServiceMode,
    setStoreLocatorCallbackUrl,
    uniqOfferId,
  ]);

  const onCancelSwitchServiceMode = useCallback(() => {
    setShowSwitchServiceModeDialog(false);
  }, []);

  const onConfirmReadyToRedeem = useCallback(
    (isLockedOffer = false) => {
      const inResturantLoyaltyEnabled =
        inRestaurantLoyaltyEnabledAtRestaurant || enableLoyaltyRedeemAllStores;

      onConfirmRedemption(offer, isLockedOffer);
      setRedeemedOffer(offer);
      setShowReadyToRedeemDialog(false);
      setRestaurantRedemptionStartTime(Date.now());

      hapticNotification({ type: HapticsNotificationType.SUCCESS });

      if (
        !hasLegacyOffer &&
        inResturantLoyaltyEnabled &&
        enableOfferRedemption &&
        isMobile() &&
        !checkIsCBAOffer(offer)
      ) {
        redeemRestaurantOffer(isLockedOffer);
        return;
      }

      setShowRestaurantRedemptionModal(true);
    },
    [
      inRestaurantLoyaltyEnabledAtRestaurant,
      enableLoyaltyRedeemAllStores,
      onConfirmRedemption,
      offer,
      setRedeemedOffer,
      hasLegacyOffer,
      enableOfferRedemption,
      redeemRestaurantOffer,
    ]
  );

  const onCancelReadyToRedeem = useCallback(
    (isLockedOffer = false) => {
      onCancelRedemption(isLockedOffer);
      setShowReadyToRedeemDialog(false);
    },
    [onCancelRedemption]
  );

  const onDismissRestaurantRedemption = useCallback(() => {
    setRestaurantRedemptionStartTime(0);
    setShowRestaurantRedemptionModal(false);
    setRedeemedOffer(null);

    navigate(offersUrl);
  }, [navigate, offersUrl, setRedeemedOffer]);

  useEffect(() => {
    if (redemptionInProgress) {
      setRedemptionInProgress(false);
      attemptOfferRedemption({ redemptionMethod: RedemptionMethod.restaurant });
    }
  }, [redemptionInProgress, attemptOfferRedemption, setRedemptionInProgress]);

  return useMemo(
    () => ({
      attemptOfferRedemption,
      loadingOfferConfigs,
      onConfirmSwitchOffers,
      onConfirmSwitchServiceMode,
      onCancelSwitchOffers,
      onCancelSwitchServiceMode,
      onConfirmReadyToRedeem,
      onCancelReadyToRedeem,
      onDismissRestaurantRedemption,
      showConfirmSwitchOffersDialog,
      showReadyToRedeemDialog,
      showRestaurantRedemptionModal,
      showSwitchServiceModeDialog,
      restaurantRedemptionStartTime,
      redemptionMethod,
    }),
    [
      attemptOfferRedemption,
      loadingOfferConfigs,
      onConfirmSwitchOffers,
      onConfirmSwitchServiceMode,
      onCancelSwitchOffers,
      onCancelSwitchServiceMode,
      onConfirmReadyToRedeem,
      onCancelReadyToRedeem,
      onDismissRestaurantRedemption,
      showConfirmSwitchOffersDialog,
      showReadyToRedeemDialog,
      showRestaurantRedemptionModal,
      showSwitchServiceModeDialog,
      restaurantRedemptionStartTime,
      redemptionMethod,
    ]
  );
};
